import { createElement } from 'react'
import * as Yup from 'yup'
import { TFunction } from 'i18next'
import { CountryCode } from '@carsys/enums/country-code'
import {
  Condition,
  EnumFormFieldType,
  FieldDependency,
  FormField,
  FormFieldSelectOptions,
  IProductWithState,
  OrderFormSchema
} from '@cbgms/api'
import { Checkbox } from '../../../Checkbox'
import { Dropdown } from '../../../Dropdown'
import { HiddenField } from '../../../HiddenField'
import { NumericInput } from '../../../NumericInput'
import { TextField } from '../../../TextField'
import { RadioGroupWrapper } from '../../components/RadioGroupWrapper'
import { LicensePlateWithTextField } from '@carsys/form/src/formbuilder/components/LicensePlateWithTextfield'
import { ToggleButtonGroupWrapper } from '@carsys/form/src/formbuilder/components/ToggleButtonGroupWrapper'
import { QuantityWithPrices } from '@carsys/form/src/formbuilder/components/QuantityWithPrices'

export interface ConditionalFields {
  condition: (field: FormField) => boolean
  component: (field: FormField, value: any) => JSX.Element
}

export interface OrderFormData {
  [key: string]: string
}

// sectionMapper takes a schema and turns that into a array of sections and fields.
// each section and field should be rendered by the consumer
export function sectionMapper(schema: OrderFormSchema, product: IProductWithState | null) {
  return schema.Form.flatMap(section => {
    const fields = section.Fields?.map(field => {
      const checkDependencies = (formValues: any) => evaluateCondition(field.Dependencies, formValues)
      return {
        id: field.ID,
        checkDependencies,
        Component: fieldMapper(field, product)
      }
    })
    return [{ SectionName: section.SectionName, fields }]
  })
}

export function fieldMapper(field: FormField, product: IProductWithState | null) {
  const { Type, Placeholder, ID, Description, Validation, Options } = field
  const fieldKeys = {
    Select: Dropdown,
    SelectButtonGroup: ToggleButtonGroupWrapper,
    Text: TextField,
    Number: NumericInput,
    Hidden: HiddenField,
    Checkbox,
    RadioButton: RadioGroupWrapper,
    LicensePlate: LicensePlateWithTextField
  }

  const mapToDropdownOptions = (options: FormFieldSelectOptions[]) => {
    if (options.length <= 0) return undefined
    return options.map(option => ({ text: option.Display, value: option.Value }))
  }

  const mapToRadioOptions = (options: FormFieldSelectOptions[]) => {
    if (options.length <= 0) return []
    return options.map(option => ({ label: option.Display, value: option.Value }))
  }

  return (): React.ReactNode => {
    if (typeof fieldKeys[Type] !== 'undefined') {
      if (Type === EnumFormFieldType.Number && ID === 'quantity') {
        // quantity is a special case, we want to show prices as well
        const quantityWithPricesProps = {
          product,
          name: ID,
          label: Description,
          helperText: Placeholder || ' ',
          required: Validation.Required
        }
        return createElement(QuantityWithPrices, quantityWithPricesProps, undefined)
      }

      if (Type === EnumFormFieldType.Checkbox) {
        const checkboxProps = {
          label: Description,
          name: ID,
          helperText: ' '
        }

        return createElement(fieldKeys[Type], checkboxProps, undefined)
      }

      if (Type === EnumFormFieldType.RadioButton) {
        const radioButtonProps = {
          label: Description,
          name: ID,
          options: mapToRadioOptions(Options),
          helperText: ' '
        }
        return createElement(fieldKeys[Type], radioButtonProps, undefined)
      }

      if (Type === EnumFormFieldType.LicensePlateText) {
        return createElement(
          fieldKeys[Type],
          {
            label: Description,
            name: ID,
            required: Validation.Required,
            country: CountryCode.NL, // this is hardcoded for now, fix this in future story
            size: 'large',
            product
          },
          undefined
        )
      }

      if (Type === EnumFormFieldType.SelectButtonGroup) {
        return createElement(
          fieldKeys[Type],
          {
            label: Description,
            name: ID,
            exclusive: true,
            color: 'primary',
            required: Validation.Required,
            helperText: ' ',
            options: mapToRadioOptions(Options)
          },
          undefined
        )
      }

      const defaultProps = {
        label: Description,
        name: ID,
        options: mapToDropdownOptions(Options),
        helperText: Placeholder || ' ',
        required: Validation.Required
      }
      return createElement(fieldKeys[Type], defaultProps, undefined)
    }
    return null
  }
}

export function evaluateCondition(condition: Condition | null, formValues: any): boolean {
  if (!condition) {
    return true
  }
  switch (condition.LogicalOperator) {
    case 'OR':
      return !!condition.Values.find(val => evaluateOperator(val, formValues))
    case 'AND':
      return !condition.Values.find(val => !evaluateOperator(val, formValues))
    default:
      console.error('unknown logical operator', condition)
      return false
  }
}

function evaluateOperator(val: FieldDependency, formValues: any): boolean {
  if (val.InnerCondition) {
    return evaluateCondition(val.InnerCondition, formValues)
  }
  switch (val.Operator) {
    case 'Equal':
      return formValues[val.ID] === val.Value
    case 'NotEqual':
      return formValues[val.ID] !== val.Value
    case 'GreaterThanEqual':
      return formValues[val.ID] >= val.Value
    default:
      console.error('unknown operator', val)
      return false
  }
}

// validationMapper turns a schema into a yup validation object
export function validationMapper(schema: OrderFormSchema, t: TFunction<'translation', undefined>) {
  function createYupSchema(yupSchema: any, { ID, Validation, Dependencies }: FormField) {
    const validationErrorMessage = Validation.ValidationFailed ? Validation.ValidationFailed : t('global.field_does_not_match_pattern')

    let validator = Yup.string()
    if (Validation.Required) {
      validator = validator.test(`${ID}-is-visible`, t('global.field_is_required'), function (val) {
        const required = evaluateCondition(Dependencies, this.parent)
        if (required && !val) {
          return false
        }
        return true
      })
    }

    if (Validation.ValidationRule) {
      const validationErrorMsg =
        Validation.ValidationRule === '^[1-9]\\d*$'
          ? t('global.quantity_must_be_greater_than_zero')
          : Validation.ValidationRule === '^[1-2]$'
          ? t('global.max_quantity_allowed_is_2')
          : Validation.ValidationRule === '^\\d{2}$'
          ? t('global.licensePlateDuplicateCodeErr')
          : Validation.ValidationRule === '^\\d{10}$'
          ? t('global.laminationCodeErr')
          : Validation.ValidationRule === '^\\d{5}$'
          ? t('global.businessInventoryNumberErr')
          : Validation.ValidationRule === '^\\d{4}$'
          ? t('global.businessInventoryCardNumberErr')
          : Validation.ValidationRule === '^\\d{4}$|^\\d{6}$'
          ? t('global.plateIssuanceCodeErr')
          : Validation.ValidationRule === '^.{9}$'
          ? t('global.passportErr')
          : validationErrorMessage
      validator = validator.matches(new RegExp(Validation.ValidationRule), validationErrorMsg)
    }

    yupSchema[ID] = validator
    return yupSchema
  }

  const fields = schema.Form.flatMap(section => section?.Fields)
  const yupSchema = fields.reduce(createYupSchema, {})
  return Yup.object().shape(yupSchema)
}

// initialValueMapper takes a shema and return a formik initial values oject
// each field might have a value in the 'schema' OR a value passed as formData directly to the component.
export function initialValueMapper(schema: OrderFormSchema, formData: OrderFormData) {
  const fields = Object.fromEntries(schema.Form.flatMap(section => section.Fields.map(field => [field.ID, field.Value])))
  const values = { ...fields }
  for (const key in formData) {
    // Only map initial values that exist on this form
    if (key in fields) {
      values[key] = formData[key]
    }
  }
  return values
}
